<template>
  <div class="ddl-list">
    <p v-if="!documents.length" class="ddl-empty">
      Nenhum documento enviado ainda.
    </p>

    <div v-for="doc in documents" :key="doc.id" class="card ddl-item">
      <div class="ddl-item__header">
        <span class="ddl-item__label">{{ labelFor(doc.document_type) }}</span>
        <span class="badge badge--no-dot" :class="statusBadgeClass(doc)">
          {{ statusLabel(doc) }}
        </span>
      </div>

      <p v-if="doc.status === 'rejected'" class="ddl-item__rejection">
        {{ doc.rejected_motivation }}
      </p>

      <template v-if="hasBackFor(doc)">
        <div class="ddl-item__view">
          <a
            v-if="doc.front"
            href="#"
            class="ddl-item__view-link"
            @click.prevent="view(doc, 'front')"
          >
            Ver frente enviada
          </a>
          <a
            v-if="doc.back"
            href="#"
            class="ddl-item__view-link"
            @click.prevent="view(doc, 'back')"
          >
            Ver verso enviado
          </a>
        </div>
      </template>
      <a
        v-else-if="doc.front"
        href="#"
        class="ddl-item__view-link ddl-item__view-link__front-only"
        @click.prevent="view(doc, 'front')"
      >
        Visualizar documento enviado
      </a>

      <div v-if="doc.status !== 'accepted'" class="ddl-item__actions">
        <button
          class="btn btn--ghost btn--sm ddl-item__edit"
          @click="handleEditClick(doc)"
        >
          Editar
        </button>
        <button
          class="btn btn--danger btn--sm ddl-item__remove"
          @click="remove(doc)"
        >
          Remover
        </button>
      </div>
    </div>

    <BModal
      id="ddl-reenvio-modal"
      size="md"
      centered
      hide-footer
      title="Reenviar documento"
    >
      <DoctorDocumentUploadFields
        v-if="editingDoc"
        :has-back="hasBackFor(editingDoc)"
        :loading="loading"
        partial
        submit-label="Reenviar"
        @submit="handleReenvioSubmit"
      />
    </BModal>

    <DoctorDocumentPreviewModal ref="previewModal" />
  </div>
</template>

<script>
import { doctorDocumentMethods } from "../state/helpers";
import DoctorDocumentUploadFields from "./DoctorDocumentUploadFields.vue";
import DoctorDocumentPreviewModal from "./DoctorDocumentPreviewModal.vue";

export default {
  name: "DoctorDocumentsList",
  components: { DoctorDocumentUploadFields, DoctorDocumentPreviewModal },
  props: {
    documents: { type: Array, default: () => [] },
    documentTypes: { type: Array, default: () => [] }
  },
  data() {
    return {
      editingDoc: null
    };
  },
  methods: {
    ...doctorDocumentMethods,

    labelFor(type) {
      const config = this.documentTypes.find(d => d.type === type);
      return config ? config.label : type;
    },

    hasBackFor(doc) {
      const config = this.documentTypes.find(d => d.type === doc.document_type);
      return !!(config && config.hasBack);
    },

    handleEditClick(doc) {
      this.editingDoc = doc;
      this.$bvModal.show("ddl-reenvio-modal");
    },

    async handleReenvioSubmit(payload) {
      try {
        await this.updateDocument({
          id: this.editingDoc.id,
          doctorDocument: {
            document_type: this.editingDoc.document_type,
            ...payload
          }
        });
        this.$bvModal.hide("ddl-reenvio-modal");
        this.editingDoc = null;
      } catch (error) {
        swal.fire({
          title: "Erro",
          text: "Não foi possível reenviar o documento. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    },

    statusLabel(doc) {
      const map = {
        in_analysis: "Em análise",
        accepted: "Aceito",
        rejected: "Rejeitado"
      };
      return map[doc.status] || doc.status;
    },

    statusBadgeClass(doc) {
      return (
        {
          in_analysis: "badge--warning",
          accepted: "badge--success",
          rejected: "badge--danger"
        }[doc.status] || "badge--warning"
      );
    },

    view(doc, side = "front") {
      const title =
        side === "back"
          ? `Verso — ${this.labelFor(doc.document_type)}`
          : `Frente — ${this.labelFor(doc.document_type)}`;
      this.$refs.previewModal.open(doc.id, side, title);
    },

    async remove(doc) {
      const result = await swal.fire({
        title: "Remover documento?",
        text: "Você poderá enviar um novo documento para este item depois.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar"
      });

      if (!result.isConfirmed) return;

      try {
        await this.deleteDocument(doc.id);
      } catch (error) {
        swal.fire({
          title: "Erro",
          text: "Não foi possível remover o documento. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    }
  },
  computed: {
    loading() {
      try {
        return this.$store.getters["doctorDocument/loading"] || false;
      } catch {
        return false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ddl-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin: 27px auto auto auto;
}

.ddl-empty {
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  text-align: center;
  padding: var(--space-8) 0;
}

.ddl-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-5);

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-3);
  }

  &__label {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
  }

  &__rejection {
    margin: 0;
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--color-danger-light);
    color: var(--color-danger);
    font-size: var(--font-size-xs);
  }

  &__view {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  &__view-link {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-semibold);
    color: var(--color-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &__front-only {
      margin: 0px auto;
    }
  }

  &__actions {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-1);
    justify-content: left;
  }
}
</style>
